<template>
    <div class="col-sm-12"  style="border: 1px solid #BCBCBC; border-radius: .5em;">
        <div class="col-sm-12 nopads">
            <div
                v-if="loading"
                class="col-12 text-center"
                style="height: 90vh; padding-top: 44vh;"
            >
                <h4 class="form-header">
                    {{ $t('contract_diary.loading_preview') }}
                </h4>
            </div>
        </div>
        <div v-if="result" class="d-flex flex-column" style="height: 42em">
            <!-- Header -->
            <header class="flex-shrink-0 p-3 border-bottom">
                <div class="row">
                    <div class="col-10">
                        <h4 class="form-header pt-0">
                            {{ $t('contracts.external.work_diary') }}
                        </h4>
                    </div>
                    <div class="col-2 d-flex justify-content-end">
                        <font-awesome-icon
                            v-if="showEdit"
                            icon="edit"
                            class="print-icon ml-auto"
                            style="margin-right: .5em"
                            @click.stop="$emit('edit')"
                        />
                    </div>
                </div>
            </header>

            <!-- Scrollable content -->
            <main class="flex-grow-1 overflow-auto p-3">
                <div class="row">
                    <span class="item-details">{{
                            getDateAsString(new Date(result.tyomaapaivakirja.tunniste.paivamaara))
                        }}</span>
                </div>
                <div class="row">
                    <span class="item-details">{{ result.otsikko.lahettaja.jarjestelma }}</span>
                </div>
                <div class="row">
                    <span class="item-details">{{ result.otsikko.lahettaja.organisaatio.nimi }}</span>
                </div>
                <div class="row pb-3">
                    <span class="item-details">{{ result.otsikko.lahettaja.organisaatio.ytunnus }}</span>
                </div>
                <!-- Duty Officer -->
                <span class="span-title mt-3">{{ $t('contract_diary.duty_officer') }}</span>
                <table class="table table-sm">
                    <tbody>
                    <tr
                        v-for="(item, index) in result.tyomaapaivakirja['paivystajan-tiedot']"
                        :key="index"
                    >
                        <td>{{ toLocalTime(item.paivystaja.aloitus) }}</td>
                        <td>{{ toLocalTime(item.paivystaja.lopetus) }}</td>
                        <td>{{ item.paivystaja.nimi }}</td>
                    </tr>
                    </tbody>
                </table>
                <!-- Weather data -->
                <span class="span-title mt-4">{{ $t('weather_station.data_title') }}</span>
                <table class="table table-sm">
                    <thead>
                    <tr>
                        <th>{{ $t('contracts.external.time') }}</th>
                        <th>{{ $t('contracts.external.station') }}</th>
                        <th>{{ $t('contracts.external.air_temp') }}</th>
                        <th>{{ $t('contracts.external.road_temp') }}</th>
                        <th>{{ $t('contracts.external.avg_wind') }}</th>
                        <th>{{ $t('contracts.external.rain_type') }}</th>
                        <th>{{ $t('contracts.external.rain_amount') }}</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr
                        v-for="(item, index) in result.tyomaapaivakirja.saatiedot"
                        :key="index"
                    >
                        <td>{{ toLocalTime(item.saatieto.havaintoaika) }}</td>
                        <td>{{ item.saatieto['aseman-tunniste'] }}</td>
                        <td>{{ item.saatieto['ilman-lampotila'] }}</td>
                        <td>{{ item.saatieto['tien-lampotila'] }}</td>
                        <td>{{ item.saatieto['keskituuli'] }}</td>
                        <td>{{ item.saatieto['sateen-olomuoto'] }}</td>
                        <td>{{ item.saatieto['sadesumma'] }}</td>
                    </tr>
                    </tbody>
                </table>
                <!-- Exceptional weather data -->
                <span class="span-title mt-4">{{ $t('contract_diary.exceptional_weather') }}</span>
                <table
                    v-if="result.tyomaapaivakirja['poikkeukselliset-saahavainnot'].length > 0"
                    class="table table-sm"
                >
                    <thead>
                    <tr>
                        <th>{{ $t('contract_diary.date') }}</th>
                        <th>{{ $t('contract_diary.description') }}</th>
                        <th>{{ $t('contract_diary.location') }}</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(item, index) in result.tyomaapaivakirja['poikkeukselliset-saahavainnot']" :key="index">
                        <td>{{ toLocalTime(item['poikkeuksellinen-saahavainto'].havaintoaika) }}</td>
                        <td>{{ item['poikkeuksellinen-saahavainto'].kuvaus }}</td>
                        <td>{{ item['poikkeuksellinen-saahavainto'].paikka }}</td>
                    </tr>
                    </tbody>
                </table>
                <span v-if="result.tyomaapaivakirja['poikkeukselliset-saahavainnot'].length < 1">-</span>
                <!-- Fleet use -->
                <span class="span-title mt-4">{{ $t('contract_diary.fleet_use') }}</span>
                <table v-if="result.tyomaapaivakirja['kaluston-kaytto'].length > 0" class="table table-sm">
                    <tbody>
                    <tr>
                        <td>Työkoneita</td>
                        <td>{{ result.tyomaapaivakirja['kaluston-kaytto'][0]['kalusto']['tyokoneiden-lkm'] }}</td>
                    </tr>
                    <tr>
                        <td>Lisäkalusto</td>
                        <td>{{ result.tyomaapaivakirja['kaluston-kaytto'][0]['kalusto']['lisakaluston-lkm'] }}</td>
                    </tr>
                    </tbody>
                </table>
                <!-- Operations -->
                <span class="span-title mt-4">{{ $t('contract_diary.road_operations') }}</span>
                <div
                    v-if="result.tyomaapaivakirja['tieston-toimenpiteet'].length > 0"
                    class="nopads"
                >
                    <table v-for="(item, index) in result.tyomaapaivakirja['tieston-toimenpiteet']" :key="index"
                           class="table table-sm">
                        <tbody>
                        <tr
                            v-for="(work, index) in item['tieston-toimenpide']['tehtavat']"
                            :key="index"
                        >
                            <td>{{ work.tehtava.id }} {{ work.tehtava.kuvaus }}</td>
                        </tr>
                        </tbody>
                    </table>
                </div>
                <span v-else>-</span>
                <!-- Other operations -->
                <span class="span-title mt-4">{{ $t('contract_diary.other_operations') }}</span>
                <div
                    v-if="result.tyomaapaivakirja['tieston-muut-toimenpiteet'].length > 0"
                    class="nopads"
                >
                    <table v-for="(item, index) in result.tyomaapaivakirja['tieston-muut-toimenpiteet']" :key="index"
                           class="table table-sm">
                        <tbody>
                        <tr
                            v-for="(work, index) in item['tieston-muu-toimenpide']['tehtavat']"
                            :key="index"
                        >
                            <td>{{ work.tehtava.kuvaus }}</td>
                        </tr>
                        </tbody>
                    </table>
                </div>
                <span v-else>-</span>
                <!-- Traffic Control -->
                <span class="span-title mt-4">{{ $t('map.add_traffic_control') }}</span>
                <table v-if="result.tyomaapaivakirja['liikenteenohjaus-muutokset'].length > 0" class="table table-sm">
                    <tbody>
                    <tr
                        v-for="(item, index) in result.tyomaapaivakirja['liikenteenohjaus-muutokset']"
                        :key="index"
                    >
                        <td>{{ item['liikenteenohjaus-muutos'].kuvaus }}</td>
                    </tr>
                    </tbody>
                </table>
                <span v-else>-</span>
                <!-- Accidents -->
                <span class="span-title mt-4">{{ $t('contract_diary.accidents') }}</span>
                <table v-if="result.tyomaapaivakirja['onnettomuudet'].length > 0" class="table table-sm">
                    <tbody>
                    <tr
                        v-for="(item, index) in result.tyomaapaivakirja['onnettomuudet']"
                        :key="index"
                    >
                        <td>{{ item['onnettomuus'].kuvaus }}</td>
                    </tr>
                    </tbody>
                </table>
                <span v-else>-</span>
                <!-- Authorities Assistance -->
                <span class="span-title mt-4">{{ $t('contract_diary.authorities_assistance') }}</span>
                <table v-if="result.tyomaapaivakirja['viranomaisen-avustaminen'].length > 0" class="table table-sm">
                    <tbody>
                    <tr
                        v-for="(item, index) in result.tyomaapaivakirja['viranomaisen-avustaminen']"
                        :key="index"
                    >
                        <td>{{ item['viranomaisen-avustus'].kuvaus }}</td>
                        <td>{{ item['viranomaisen-avustus'].tunnit }} h</td>
                    </tr>
                    </tbody>
                </table>
                <span v-else>-</span>
                <!-- Feedback -->
                <span class="span-title mt-4">{{ $t('contract_diary.feedback') }}</span>
                <table v-if="result.tyomaapaivakirja['palautteet'].length > 0" class="table table-sm">
                    <tbody>
                    <tr
                        v-for="(item, index) in result.tyomaapaivakirja['palautteet']"
                        :key="index"
                    >
                        <td>{{ item.palaute.kuvaus }}</td>
                    </tr>
                    </tbody>
                </table>
                <span v-else>-</span>
                <!-- Customer contacts -->
                <span class="span-title mt-4">{{ $t('contract_diary.customer_contacts_info_title') }}</span>
                <table v-if="result.tyomaapaivakirja['tilaajan-yhteydenotot'].length > 0" class="table table-sm">
                    <tbody>
                    <tr
                        v-for="(item, index) in result.tyomaapaivakirja['tilaajan-yhteydenotot']"
                        :key="index"
                    >
                        <td>{{ item['tilaajan-yhteydenotto']['kuvaus'] }}</td>
                    </tr>
                    </tbody>
                </table>
                <span v-else>-</span>
                <!-- Other info -->
                <span class="span-title mt-4">{{ $t('contract_diary.other_info') }}</span>
                <table v-if="result.tyomaapaivakirja['muut-kirjaukset']" class="table table-sm">
                    <tbody>
                    <tr>
                        <td>{{ result.tyomaapaivakirja['muut-kirjaukset']['kuvaus'] }}</td>
                    </tr>
                    </tbody>
                </table>
                <span v-else>-</span>
            </main>

            <!-- Footer -->
            <footer class="flex-shrink-0 p-3 border-top">
                <div class="col-sm-12 d-flex justify-content-end">
                    <button class="btn btn-outline-primary form-button" @click.stop="$emit('edit')">
                        {{ $t('common.edit')}}
                    </button>
                    <button
                        class="btn btn-success form-button"
                        :disabled="loading"
                        @click.stop="submitWorkDiaryReport"
                    >
                        {{ $t('contracts.external.submit_report') }}
                    </button>
                </div>
            </footer>
        </div>
    </div>
    <div
        v-if="loading"
        id="loader"
        class="spinner"
    />
</template>

<script>
import {restApi} from '../../mixins/RestApiMixin'
import {timeUtils} from "@/components/mixins/TimeUtils";
import {inject} from "vue";

export default {
    name: 'ContractWorkDiary',
    mixins: [restApi, timeUtils],
    props: {
        contract: {
            type: Object,
            default: null
        },
        date: {
            type: Date,
            default: new Date()
        },
        showPreview: {
            type: Boolean,
            default: false
        },
        showEdit: {
            type: Boolean,
            default: true
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return {showAlert};
    },
    data: function () {
        return {
            loading: false,
            result: null
        }
    },
    computed: {
        isDateToday() {
            if (!this.date) {
                return true
            }
            const today = new Date()
            return (today.getFullYear() === this.date.getFullYear() && today.getMonth() === this.date.getMonth() && today.getDate() === this.date.getDate())
        },
        isContractSelected() {
            return this.contract
        }
    },
    watch: {
        date() {
            this.fetchDiary()
        }
    },
    mounted() {
        this.fetchDiary()
    },
    methods: {
        fetchDiary() {
            this.loading = true
            this.result = null
            const dateParam = new Date(this.date)
            dateParam.setHours(12)
            let params = {
                contract: this.contract.id,
                date: dateParam.toISOString()
            }
            this.restFetchParams(this.extWorkDiaryUrl, params, this.handleResult, this.handleError)
        },

        handleResult(response) {
            this.loading = false
            if (response && response.data) {
                this.result = response.data
            }
        },

        handleError() {
            this.loading = false
            this.showAlert(this.$t('contracts.external.diary_error'))
        },

        submitWorkDiaryReport() {
            this.loading = true
            const params = {
                contract: this.contract.id,
                data: this.result
            }
            this.restAdd(this.extWorkDiaryUrl, params, () => {
                this.loading = false
                this.showAlert(this.$t('common.alert_update_success'), 'success')
                this.$emit('update')
            }, () => {
                this.loading = false
                this.showAlert(this.$t('contracts.external.report_failed'))
            })
        },
    }
}
</script>
